import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, isManager, isDesigner } from "@/context/AuthContext";
import { effectiveAttendanceMode, correctedMode } from "@/lib/attendanceMode";
import MyShiftBlock from "@/components/MyShiftBlock";
import MyAdvancesCard from "@/components/cashbox/MyAdvancesCard";
import { ListChecks, HardHat, ChevronRight, BookOpen } from "lucide-react";
import { PageHeader, useIsMobile } from "@/components/shared/PagePrimitives";
import ManagerHome from "@/components/mobile/ManagerHome";
import WorkerHome from "@/components/mobile/WorkerHome";
import DesktopHome from "@/components/home/DesktopHome";
import { Button } from "@/components/ui/button";

const czDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? iso : `${d.getDate()}. ${d.getMonth() + 1}.`;
};

function Card({ title, icon: Icon, badge, children, testid, className = "" }) {
  return (
    <div className={`bg-card border border-border rounded-[20px] p-5 ${className}`} data-testid={testid}>
      {title && (
        <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
          {Icon && <Icon size={18} className="text-brand" />} {title}
          {badge != null && <span className="text-xs font-black text-white bg-brand rounded-full px-2 py-0.5">{badge}</span>}
        </h2>
      )}
      {children}
    </div>
  );
}

function PersonalTab() {
  // 219: režim z přihlášení hned (dřív se čekalo na /daysheet/current a teprve pak
  // startovala karta směny); /me/overview ho případně opraví.
  const { user, company } = useAuth();
  const [mode, setMode] = useState(() => effectiveAttendanceMode(user, company));
  const onShiftState = useCallback((st) => setMode((m) => correctedMode(m, st?.ov)), []);
  return (
    <div className="space-y-6">
      <MyShiftBlock mode={mode} onState={onShiftState} />
      <MyAdvancesCard />
    </div>
  );
}

// Přehled projektanta (173): nemá docházku ani peníze — jen jeho otevřené úkoly
// a zakázky, kde je v týmu. Server vrací jen jeho zakázky a úkoly v nich.
const TASK_PILL = {
  "Nový": "bg-secondary text-muted-foreground",
  "V řešení": "bg-brand/15 text-brand",
  "Hotovo": "bg-success/15 text-success",
};
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Prague" });

function DesignerHome({ user }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState(null);
  const [projects, setProjects] = useState(null);
  useEffect(() => {
    api.get("/tasks?status=open").then((r) => setTasks(r.data || [])).catch(() => setTasks([]));   // 218: schválené tu nejsou potřeba
    api.get("/projects/options").then((r) => setProjects(r.data || [])).catch(() => setProjects([]));
  }, []);

  const today = todayIso();
  const mine = (tasks || []).filter((t) => t.status !== "Schváleno"
    && ((t.assignees || []).includes(user.id) || t.created_by === user.id));
  const toApprove = (tasks || []).filter((t) => t.status === "Hotovo");
  const active = (projects || []).filter((p) => !["Dokončeno", "Fakturováno", "Záruka"].includes(p.status));

  const row = "w-full flex items-center gap-3 py-2.5 px-2 -mx-2 rounded-[12px] text-left hover:bg-secondary/50 min-h-[48px]";
  const skeleton = <div className="h-24 rounded-[14px] bg-secondary animate-pulse motion-reduce:animate-none" />;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="designer-home">
      <Card title="Moje úkoly" icon={ListChecks} badge={mine.length || null} testid="designer-tasks">
        {tasks === null ? skeleton : mine.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nemáte žádný otevřený úkol.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {mine.slice(0, 8).map((t) => {
              const late = t.deadline && t.deadline.slice(0, 10) < today && t.status !== "Hotovo";
              return (
                <button key={t.id} type="button" onClick={() => navigate(`/ukoly/${t.id}`)} className={row} data-testid={`designer-task-${t.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate">{t.title}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {[t.project_name, t.deadline && `do ${czDate(t.deadline)}`].filter(Boolean).join(" · ") || "Bez zakázky"}
                    </span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${late ? "bg-destructive/15 text-destructive" : (TASK_PILL[t.status] || TASK_PILL["Nový"])}`}>
                    {late ? "Po termínu" : t.status}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
          {toApprove.length > 0 && (
            <span className="text-sm text-muted-foreground" data-testid="designer-to-approve">
              Ke schválení: <b className="text-foreground">{toApprove.length}</b>
            </span>
          )}
          <Button variant="outline" className="w-full sm:w-auto sm:ml-auto" onClick={() => navigate("/ukoly")}>Všechny úkoly</Button>
        </div>
      </Card>

      <Card title="Moje zakázky" icon={HardHat} badge={active.length || null} testid="designer-projects">
        {projects === null ? skeleton : active.length === 0 ? (
          <p className="text-sm text-muted-foreground">Zatím vás nikdo nepřiřadil k zakázce.</p>
        ) : (
          <div className="divide-y divide-border/60">
            {active.map((p) => (
              <div key={p.id} className="flex items-center gap-1">
                <button type="button" onClick={() => navigate(`/zakazky/${p.id}`)} className={`${row} flex-1 min-w-0`} data-testid={`designer-project-${p.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold truncate">{p.code ? `${p.code} · ` : ""}{p.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">
                      {[p.status, p.end_date && `do ${czDate(p.end_date)}`].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                  <ChevronRight size={16} className="shrink-0 text-muted-foreground" />
                </button>
                <button type="button" onClick={() => navigate(`/zakazky/${p.id}?tab=diary`)} aria-label="Deník" title="Deník"
                  className="w-11 h-11 shrink-0 grid place-items-center rounded-[12px] text-muted-foreground hover:bg-secondary/50 hover:text-brand">
                  <BookOpen size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const manager = isManager(user);
  // Telefon (196): vedení má vlastní přehled; počítač a tablet (236): nový úvod se snímky
  // místo dřívějších záložek Osobní / Pracovní.
  const isMobile = useIsMobile();
  if (isMobile && manager && !isDesigner(user)) {
    return <div data-testid="dashboard-page"><ManagerHome /></div>;
  }
  // Telefon (199/202): dělník, subdodavatel a skladník — karta směny s předvolenou stavbou a rychlé akce
  if (isMobile && ["WORKER", "SUBCONTRACTOR", "WAREHOUSE"].includes(user?.role)) {
    return <div data-testid="dashboard-page"><WorkerHome /></div>;
  }
  // Počítač a tablet (246): i dělník, skladník a subdodavatel mají úvod s pruhem
  if (manager || ["WORKER", "SUBCONTRACTOR", "WAREHOUSE"].includes(user?.role)) {
    return <div className="pb-8" data-testid="dashboard-page"><DesktopHome /></div>;
  }

  return (
    <div className="space-y-6 pb-[calc(5rem+env(safe-area-inset-bottom))] md:pb-0" data-testid="dashboard-page">
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="dash-header">
        <PageHeader title="Přehled" />
        <span className="hidden sm:block text-xs text-muted-foreground tabular-nums">
          {new Intl.DateTimeFormat("cs-CZ", { weekday: "long", day: "numeric", month: "numeric", year: "numeric", timeZone: "Europe/Prague" }).format(new Date())}
          {" · "}{new Intl.DateTimeFormat("cs-CZ", { hour: "numeric", minute: "2-digit", timeZone: "Europe/Prague" }).format(new Date())}
          {" · "}{user?.name}
        </span>
      </div>

      {isDesigner(user) ? <DesignerHome user={user} /> : <PersonalTab />}
    </div>
  );
}
